import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import { resolveUserSubmitErrorMessage } from "../../../../api/submitRequestErrors";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import {
  REGISTRATION_STEP_TITLE_PROPS,
  useRegistrationStepFocus,
} from "../../../../hook/useRegistrationStepFocus";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import {
  buildCoverageResultFromEligibility,
} from "../registrationInsuranceMock";
import { isEligibilityVerified } from "../insuranceProviders";
import {
  getRegistrationData,
  isCardOnFileComplete,
  updateRegistrationData,
} from "../../registrationSetupStorage";
import { extractPublishableKey } from "../../CardOnFile/RegistrationCardOnFile/RegistrationCardOnFile.validator";
import {
  markInsuranceDetailsReturnToCoverage,
  clearCoverageReturnToInsuranceVerification,
  registrationStepBack,
} from "../../registrationNavigation";
import {
  redirectIfProviderUnavailableBeforeApi,
  redirectIfProviderUnavailableFromResponse,
} from "../../registrationRdAvailability";

const COVERAGE_STEP = REGISTRATION_SETUP_STEP_META.INSURANCE_COVERAGE_FOUND;

const COVERAGE_DETAIL_FIELDS = [
  { key: "memberId", labelKey: "MEMBER_ID" },
  { key: "groupNumber", labelKey: "NUTRITION_COMMON_LBL_GROUP_NUMBER" },
  { key: "coverageSince", labelKey: "NUTRITION_INSDISC_LBL_COVERAGE_SINCE" },
  { key: "planType", labelKey: "NUTRITION_INSDISC_LBL_PLAN_TYPE" },
];

const RegistrationInsuranceCoverageFound = ({ history }) => {
  const { t, i18n } = useTranslation();
  const [coverage, setCoverage] = useState(null);
  const stepTitleRef = useRegistrationStepFocus();

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    const parsed = getRegistrationData();
    if (!parsed.firstName && !parsed.email) {
      history.replace("/register/account/payment");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }

    if (
      parsed.insuranceEligibilityResult &&
      !isEligibilityVerified(parsed.insuranceEligibilityResult)
    ) {
      history.replace("/register/account/insurance/details");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }

    if (
      parsed.insuranceEligibilityResult &&
      isEligibilityVerified(parsed.insuranceEligibilityResult)
    ) {
      const resolvedCoverage = buildCoverageResultFromEligibility(
        parsed.insuranceEligibilityResult,
        parsed.insuranceVerification,
        parsed.insuranceManual,
        parsed.insuranceProviderOptions
      );
      if (resolvedCoverage) {
        setCoverage(resolvedCoverage);
      } else {
        history.replace("/register/account/insurance");
      }
    } else if (parsed.coverageResult) {
      setCoverage(parsed.coverageResult);
    } else {
      history.replace("/register/account/insurance");
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContinue = async () => {
    if (isSubmitting) return;
    const parsed = getRegistrationData();
    if (!parsed || !parsed.firstName || !parsed.email) {
      history.replace("/register/account/payment");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isCardOnFileComplete(parsed)) {
        clearCoverageReturnToInsuranceVerification();
        history.replace("/register/account/card");
        return;
      }

      if (redirectIfProviderUnavailableBeforeApi(history, parsed)) {
        return;
      }

      const tokenRes =
        await AuthenticationApiSerivce.getRegistrationStripePublishableToken(
          i18n.language
        );

      if (redirectIfProviderUnavailableFromResponse(history, tokenRes)) {
        return;
      }

      if (!tokenRes?.status) {
        message.error(
          tokenRes?.message || t("NUTRITION_INSDISC_ERR_LOAD_PAYMENT_CONFIG")
        );
        return;
      }

      const stripePublishableKey = extractPublishableKey(tokenRes);
      if (!stripePublishableKey) {
        message.error(t("NUTRITION_INSDISC_ERR_LOAD_PAYMENT_CONFIG"));
        return;
      }

      updateRegistrationData({ stripePublishableKey });
      clearCoverageReturnToInsuranceVerification();
      history.replace("/register/account/card");
    } catch (err) {
      message.error(
        resolveUserSubmitErrorMessage(err, {
          fallback: t("NUTRITION_COMMON_ERR_TRY_AGAIN"),
          translate: t,
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!coverage) {
    return null;
  }

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={COVERAGE_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={() => registrationStepBack(history)}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card">
            <div className="content-card-scroll">
              <div className="coverage-found-banner">
                <div className="coverage-found-icon" aria-hidden>
                  ✅
                </div>
                <div>
                  <div
                    className="coverage-found-title"
                    ref={stepTitleRef}
                    {...REGISTRATION_STEP_TITLE_PROPS}
                  >
                    {t("NUTRITION_INSDISC_LBL_BANNER_TITLE")}
                  </div>
                  <div className="coverage-found-sub">
                    {t("NUTRITION_INSDISC_DESC_BANNER")}
                  </div>
                </div>
              </div>

              <div className="coverage-card">
                <div className="coverage-card-header">
                  <div>
                    {coverage.payerName ? (
                      <div className="coverage-card-payer">{coverage.payerName}</div>
                    ) : null}
                    {coverage.planName ? (
                      <div className="coverage-card-plan">{coverage.planName}</div>
                    ) : null}
                  </div>
                  {coverage.status ? (
                    <div className="coverage-card-badge">{coverage.status}</div>
                  ) : null}
                </div>
                <div className="coverage-fields">
                  {COVERAGE_DETAIL_FIELDS.map((field) =>
                    coverage[field.key] ? (
                      <div key={field.key} className="coverage-field">
                        <span className="coverage-field-label">
                          {t(field.labelKey)}
                        </span>
                        <span className="coverage-field-value">
                          {coverage[field.key]}
                        </span>
                      </div>
                    ) : null
                  )}
                </div>
              </div>

              {coverage.copayEstimate ? (
                <div className="coverage-estimate">
                  <div className="coverage-estimate-label">
                    {t("NUTRITION_INSDISC_LBL_ESTIMATE_LABEL")}
                  </div>
                  <div className="coverage-estimate-amount">
                    {coverage.copayEstimate}
                  </div>
                  <div className="coverage-estimate-sub">
                    {t("NUTRITION_INSDISC_DESC_ESTIMATE")}
                  </div>
                </div>
              ) : null}

              <div className="stedi-badge-footer" style={{ marginBottom: 20 }}>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                {t("NUTRITION_INSDISC_LBL_STEDI_BADGE2")}
              </div>

              <button
                type="button"
                className="btn-primary"
                onClick={handleContinue}
                disabled={isSubmitting}
              >
                {t("NUTRITION_INSDISC_BTN_CONFIRM")}
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  markInsuranceDetailsReturnToCoverage();
                  history.push("/register/account/insurance/details");
                }}
              >
                {t("NUTRITION_INSDISC_BTN_EDIT")}
              </button>
            </div>
          </div>
        </div>
      </RegistrationLayout>
    </RegistrationSetupWrapper>
  );
};

export default withRouter(RegistrationInsuranceCoverageFound);
